'use client'

import { createContext, useContext, useEffect, useReducer, useRef } from 'react'
import type { Cart, CartItem } from '@/lib/cart'
import { loadCart, saveCart } from '@/lib/cart'

type Action =
  | { type: 'ADD'; item: CartItem }
  | { type: 'REMOVE'; variantId: string }
  | { type: 'UPDATE_QTY'; variantId: string; quantity: number }
  | { type: 'CLEAR' }
  | { type: 'INIT'; cart: Cart }
  // Synchronizacja z serwerem (/api/koszyk/verify): aktualne ceny, usunięcie
  // niedostępnych pozycji — koszyk z localStorage po zmianie cen wpadał w pętlę 409.
  | { type: 'SYNC'; updates: Map<string, { available: boolean; price?: number }> }

// Górny limit ilości jednej pozycji — chroni przed rozdmuchaniem koszyka
// wielokrotnym „dodaj do koszyka" (spójny z walidacją w loadCart).
const MAX_QTY = 99

function reducer(cart: Cart, action: Action): Cart {
  switch (action.type) {
    case 'INIT':
      return action.cart
    case 'ADD': {
      const idx = cart.items.findIndex((i) => i.variantId === action.item.variantId)
      if (idx >= 0) {
        const items = [...cart.items]
        items[idx] = { ...items[idx], quantity: Math.min(MAX_QTY, items[idx].quantity + action.item.quantity) }
        return { items }
      }
      return { items: [...cart.items, { ...action.item, quantity: Math.min(MAX_QTY, action.item.quantity) }] }
    }
    case 'REMOVE':
      return { items: cart.items.filter((i) => i.variantId !== action.variantId) }
    case 'UPDATE_QTY': {
      if (action.quantity <= 0) {
        return { items: cart.items.filter((i) => i.variantId !== action.variantId) }
      }
      return {
        items: cart.items.map((i) =>
          i.variantId === action.variantId ? { ...i, quantity: action.quantity } : i
        ),
      }
    }
    case 'CLEAR':
      return { items: [] }
    case 'SYNC': {
      const items = cart.items
        .filter((i) => action.updates.get(i.variantId)?.available !== false)
        .map((i) => {
          const u = action.updates.get(i.variantId)
          return u?.price !== undefined && Math.abs(u.price - i.price) > 0.001 ? { ...i, price: u.price } : i
        })
      return { items }
    }
    default:
      return cart
  }
}

type CartContextValue = {
  cart: Cart
  addItem: (item: CartItem) => void
  removeItem: (variantId: string) => void
  updateQty: (variantId: string, quantity: number) => void
  clearCart: () => void
  syncCart: (updates: Map<string, { available: boolean; price?: number }>) => void
}

const CartContext = createContext<CartContextValue | null>(null)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, dispatch] = useReducer(reducer, { items: [] })
  const initialized = useRef(false)

  useEffect(() => {
    dispatch({ type: 'INIT', cart: loadCart() })
  }, [])

  useEffect(() => {
    // Pomiń pierwszy przebieg (początkowy pusty stan) — zapis dopiero po INIT,
    // inaczej na moment nadpisywalibyśmy localStorage pustym koszykiem.
    if (!initialized.current) {
      initialized.current = true
      return
    }
    saveCart(cart)
  }, [cart])

  return (
    <CartContext.Provider
      value={{
        cart,
        addItem: (item) => dispatch({ type: 'ADD', item }),
        removeItem: (variantId) => dispatch({ type: 'REMOVE', variantId }),
        updateQty: (variantId, quantity) => dispatch({ type: 'UPDATE_QTY', variantId, quantity }),
        clearCart: () => dispatch({ type: 'CLEAR' }),
        syncCart: (updates) => dispatch({ type: 'SYNC', updates }),
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
